/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {Controller} from 'react-hook-form'
import {useIntl} from 'react-intl'
import {InputLeftAddon} from '@chakra-ui/react'
import {
    Box,
    FormControl,
    FormLabel,
    FormErrorMessage,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    Select,
    Checkbox,
    Tooltip
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {VisibilityIcon, VisibilityOffIcon, AlertIcon, InfoIcon} from '../icons'

const Field = ({
    name,
    label,
    formLabel,
    type = 'text',
    options = [],
    rules = {},
    error,
    placeholder,
    inputProps,
    control,
    autoComplete,
    defaultValue,
    helpText,
    children,
    setValue,
    countryCodeValue,
    ...props
}) => {
    const intl = useIntl()
    const [hidePassword, setHidePassword] = useState(true)
    const [streetValue, setStreetValue] = useState('')
    const PasswordIcon = hidePassword ? VisibilityIcon : VisibilityOffIcon
    const passwordIconLabel = hidePassword
        ? intl.formatMessage({
              id: 'field.password.assistive_msg.show_password',
              defaultMessage: 'Show password'
          })
        : intl.formatMessage({
              id: 'field.password.assistive_msg.hide_password',
              defaultMessage: 'Hide password'
          })
    const inputType =
        type === 'password' && hidePassword ? 'password' : type === 'password' ? 'text' : type

    const ImageOption = ({data}) => (
        <div>
            <img
                src={data.image}
                alt={data.label}
                style={{marginRight: 10, width: 20, height: 20}}
            />
            {data.label}
        </div>
    )

    /**************** Google Address Suggestions Starts *******************/
    const searchInput = useRef(null)

    /**
     * Read address suggestions and update it into the form fields
     */
    const readAddressSuggestions = (place) => {
        const places = place?.address_components
        const countryCode = document.getElementById('countryCode')
        const city = document.getElementById('city')
        const street = document.getElementById('address1')
        const postalCode = document.getElementById('postalCode')

        places.forEach((component) => {
            const types = component.types

            types.forEach((type) => {
                if (type === 'country') {
                    const country = Object.values(countryCode).find(
                        (country) => country.value === component.short_name
                    )

                    if (country) {
                        country.selected = true

                        countryCode.dispatchEvent(
                            new Event('change', {
                                bubbles: true,
                                cancelable: true
                            })
                        )
                    }
                }

                if (type === 'street_number') {
                    setStreetValue(component.long_name)
                    street.value = component.long_name
                }

                if (type === 'route') {
                    setStreetValue((prevStreetValue) => {
                        const updatedStreetValue = `${prevStreetValue || ''} ${component.long_name}`
                        onChangeFieldValue('address1', updatedStreetValue)
                        street.value = updatedStreetValue
                    })
                }

                if (
                    type === 'locality' ||
                    type === 'sublocality_level_1' ||
                    type === 'sublocality'
                ) {
                    const cityValue = component.long_name
                    city.value = component.long_name
                    onChangeFieldValue('city', cityValue)
                }

                if (type === 'postal_code') {
                    const postalCodeValue = component.short_name
                    postalCode.value = component.short_name
                    onChangeFieldValue('postalCode', postalCodeValue)
                }
            })
        })
    }

    const onChangeFieldValue = (fieldName, value) => {
        setValue(fieldName, value, {shouldValidate: true})
    }

    const stateUpdateEvent = (place) => {
        const places = place?.address_components
        const stateCode = document.getElementById('stateCode')

        places.forEach((component) => {
            const types = component.types

            types.forEach((type) => {
                if (type === 'administrative_area_level_1') {
                    const state = Object.values(stateCode).find(
                        (state) => state.value === component.short_name
                    )

                    if (state) {
                        state.selected = true

                        stateCode.dispatchEvent(
                            new Event('change', {
                                bubbles: true,
                                cancelable: true
                            })
                        )
                    }
                }
            })
        })
    }

    useEffect(() => {
        if (countryCodeValue) {
            onChangeFieldValue('countryCode', countryCodeValue)
        }
    }, [countryCodeValue])

    /**
     * Fetch Google Address Suggestions
     */
    useEffect(() => {
        const initializeGoogleAddressSuggestions = async () => {
            if (window.google) {
                if (searchInput.current) {
                    const autocomplete = new window.google.maps.places.Autocomplete(
                        searchInput.current
                    )

                    autocomplete.addListener('place_changed', () => {
                        const place = autocomplete.getPlace()
                        readAddressSuggestions(place)
                        stateUpdateEvent(place)
                    })
                }
            }
        }
        initializeGoogleAddressSuggestions()
    }, [])
    /**************** Google Address Suggestions Ends *******************/

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            defaultValue={defaultValue}
            render={({field: {onChange, value, ref}}) => {
                const _inputProps =
                    typeof inputProps === 'function' ? inputProps({value, onChange}) : inputProps

                return (
                    <FormControl id={name} isInvalid={error}>
                        {!['checkbox', 'radio'].includes(type) &&
                            type !== 'hidden' &&
                            (formLabel || (
                                <FormLabel {...props.labelProps}>
                                    {label}
                                    {type === 'select' && name === 'countryCode' && (
                                        <Tooltip
                                            hasArrow
                                            placement="top"
                                            label={intl.formatMessage({
                                                defaultMessage:
                                                    'Looking to change the country, Please go back to cart and select the country icon.',
                                                id: 'shipping_address_selection.tooltip.countryCode'
                                            })}
                                        >
                                            <InfoIcon h="1rem" color="gray.700" boxSize={5} />
                                        </Tooltip>
                                    )}
                                </FormLabel>
                            ))}

                        <InputGroup>
                            {['text', 'password', 'email', 'phone', 'tel', 'number'].includes(
                                type
                            ) && (
                                <>
                                    {name === 'phone' && props.phone_prefix && (
                                        <>
                                            <InputLeftAddon>{props.phone_prefix}</InputLeftAddon>
                                        </>
                                    )}

                                    {name === 'address1' && (
                                        <Input
                                            ref={searchInput}
                                            type={inputType}
                                            onChange={onChange}
                                            value={value ? value : streetValue}
                                            placeholder={placeholder}
                                            autoComplete={autoComplete}
                                            {...inputProps}
                                        />
                                    )}

                                    {(name === 'city' || name === 'postalCode') && (
                                        <Input
                                            ref={ref}
                                            type={inputType}
                                            onChange={onChange}
                                            value={value}
                                            placeholder={placeholder}
                                            autoComplete={autoComplete}
                                            {...inputProps}
                                        />
                                    )}

                                    {name !== 'address1' &&
                                        name !== 'city' &&
                                        name !== 'postalCode' && (
                                            <Input
                                                ref={ref}
                                                onChange={onChange}
                                                value={value}
                                                type={inputType}
                                                placeholder={placeholder}
                                                autoComplete={autoComplete}
                                                {..._inputProps}
                                            />
                                        )}
                                </>
                            )}

                            {type === 'hidden' && (
                                <input
                                    ref={ref}
                                    onChange={onChange}
                                    value={value}
                                    type="hidden"
                                    {..._inputProps}
                                />
                            )}

                            {type === 'password' && (
                                <InputRightElement>
                                    <IconButton
                                        variant="ghosted"
                                        aria-label={passwordIconLabel}
                                        icon={<PasswordIcon color="gray.500" boxSize={6} />}
                                        onClick={() => setHidePassword(!hidePassword)}
                                    />
                                </InputRightElement>
                            )}

                            {name === 'countryCode' && props.flag_image && (
                                <Box position="absolute" mt={'5px'} ml={'8px'}>
                                    {props.flag_image}
                                </Box>
                            )}

                            {type === 'select' && (
                                <Select
                                    ref={ref}
                                    onChange={onChange}
                                    isDisabled={name === 'countryCode'}
                                    value={value}
                                    placeholder={placeholder}
                                    {..._inputProps}
                                    style={{
                                        paddingLeft: name === 'countryCode' ? '2.5rem' : ''
                                    }}
                                    components={{Option: ImageOption}}
                                >
                                    {options.map((opt) => (
                                        <option
                                            key={`${opt?.label}-${opt?.value}`}
                                            value={opt.value}
                                        >
                                            {opt?.label}
                                        </option>
                                    ))}
                                </Select>
                            )}

                            {type === 'checkbox' && (
                                <Checkbox
                                    ref={ref}
                                    onChange={(e) => onChange(e.target.checked)}
                                    isChecked={value}
                                    {..._inputProps}
                                >
                                    {formLabel || label}
                                </Checkbox>
                            )}

                            {children}
                        </InputGroup>

                        {error && type !== 'hidden' && (
                            <FormErrorMessage color="red.600">
                                <AlertIcon aria-hidden="true" mr={2} />
                                {error.message}
                            </FormErrorMessage>
                        )}

                        {helpText}
                    </FormControl>
                )
            }}
        />
    )
}

Field.propTypes = {
    name: PropTypes.string,
    label: PropTypes.string,
    labelProps: PropTypes.object,
    autoComplete: PropTypes.string,
    formLabel: PropTypes.any,
    type: PropTypes.oneOf([
        'text',
        'number',
        'password',
        'email',
        'phone',
        'tel',
        'select',
        'checkbox',
        'hidden'
    ]),
    options: PropTypes.arrayOf(PropTypes.shape({label: PropTypes.string, value: PropTypes.any})),
    rules: PropTypes.object,
    error: PropTypes.shape({message: PropTypes.string}),
    placeholder: PropTypes.string,
    inputProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    countryCodeValue: PropTypes.any,
    control: PropTypes.object,
    setValue: PropTypes.any,
    defaultValue: PropTypes.any,
    helpText: PropTypes.any,
    children: PropTypes.any,
    data: PropTypes.object,
    flag_image: PropTypes.any,
    phone_prefix: PropTypes.any,
    form: PropTypes.object
}

export default Field
